import {
  availabilitiesAtom,
  categoriesAtom,
  itemOffersAtom,
  itemsAtom,
  optionGroupsAtom,
} from "@/atoms";
import cartAtom from "@/atoms/cart";
import cartHydratedAtom from "@/atoms/cart-hydrated";
import { indoorCategoriesAtom, indoorItemOffersAtom } from "@/atoms/indoor";
import schedulingContextAtom from "@/atoms/scheduling-context";
import { tableAtom } from "@/atoms/table";
import { showToast } from "@/components/feedback";
import { useCart } from "@/hooks/use-cart";
import { errorMessage } from "@/lib/i18n/error-messages";
import { emitErrorLog } from "@/lib/otel";
import { formatRemovedItemsMessage, pruneCartAgainstCatalog } from "@/lib/prune-cart-against-catalog";
import type { CatalogSnapshot } from "@/lib/revalidate-checkout-catalog";
import { useAtomValue } from "jotai";
import { useEffect } from "react";

/**
 * Keeps a bag that is still inside its TTL honest with the live catalog: once
 * the bag is hydrated, and again every time the catalog is re-fetched, lines
 * the store no longer accepts leave the bag (named in a toast, like the
 * reorder does) and the rest is repriced. Lines that are only outside their
 * hours stay while the store sells future windows: checkout decides those.
 */
export function useCartCatalogGuard() {
  const cart = useAtomValue(cartAtom);
  const hydrated = useAtomValue(cartHydratedAtom);
  const table = useAtomValue(tableAtom);
  const deliveryItemOffers = useAtomValue(itemOffersAtom);
  const indoorItemOffers = useAtomValue(indoorItemOffersAtom);
  const deliveryCategories = useAtomValue(categoriesAtom);
  const indoorCategories = useAtomValue(indoorCategoriesAtom);
  const optionGroups = useAtomValue(optionGroupsAtom);
  const availabilities = useAtomValue(availabilitiesAtom);
  const items = useAtomValue(itemsAtom);
  const schedulingContext = useAtomValue(schedulingContextAtom);
  const { updateCart } = useCart();

  const itemOffers = table ? indoorItemOffers : deliveryItemOffers;
  const categories = table ? indoorCategories : deliveryCategories;
  const pruneOutOfSchedule = schedulingContext !== null && !schedulingContext.featureFlags.futureWindows;

  useEffect(() => {
    if (!hydrated || cart.items.length === 0 || itemOffers.length === 0) return;

    const catalog: CatalogSnapshot = { itemOffers, optionGroups, categories, availabilities, items };
    const result = pruneCartAgainstCatalog(cart, catalog, { pruneOutOfSchedule });
    if (result.removed.length === 0 && !result.pricesMoved) return;

    updateCart(result.cart);

    if (result.removed.length > 0) {
      showToast.warning(formatRemovedItemsMessage(result.removed), {
        title: result.removed.length === 1 ? "Item removido da sacola" : "Itens removidos da sacola",
      });
    } else {
      showToast.warning(errorMessage("staleCart"), { title: "Sua sacola foi atualizada" });
    }

    emitErrorLog({
      name: "cart.pruned",
      severity: "WARN",
      message: result.removed.length > 0 ? "cart lines removed against the live catalog" : "cart repriced against the live catalog",
      attributes: {
        "cart.removed_count": result.removed.length,
        "cart.kept_count": result.cart.items.length,
        "cart.prices_moved": result.pricesMoved,
        "cart.removed_reasons": [...new Set(result.removed.map((line) => line.reason))].join(","),
        "cart.removed_option_names": [
          ...new Set(
            result.removed
              .map((line) => line.optionName)
              .filter((name): name is string => !!name?.trim()),
          ),
        ].join(","),
        "cart.removed_option_group_names": [
          ...new Set(
            result.removed
              .map((line) => line.optionGroupName)
              .filter((name): name is string => !!name?.trim()),
          ),
        ].join(","),
        "cart.prune_out_of_schedule": pruneOutOfSchedule,
      },
      body: {
        removed: result.removed,
        offerIds: [...new Set(result.removed.map((line) => line.offerId))],
        optionIds: [
          ...new Set(
            result.removed
              .map((line) => line.optionId)
              .filter((id): id is string => !!id),
          ),
        ],
      },
    });
  }, [hydrated, cart, itemOffers, optionGroups, categories, availabilities, items, pruneOutOfSchedule, updateCart]);
}
